import { areAllPricesSoldOut } from './productAvailability';

const normalizeId = (value) => (value == null ? null : String(value));
const hasOwn = Object.prototype.hasOwnProperty;

const mergeProduct = (current, patch) => {
  if (!patch) return current;
  const next = { ...current, ...patch };
  if (!hasOwn.call(patch, 'Varieties')) {
    next.Varieties = current?.Varieties || [];
  }
  return next;
};

const mergeVariety = (current, patch) => {
  if (!patch) return current;
  const next = { ...current, ...patch };
  if (!hasOwn.call(patch, 'VarietyPrices')) {
    next.VarietyPrices = current?.VarietyPrices || [];
  }
  return next;
};

const mergePrice = (current, patch) => {
  if (!patch) return current;
  return { ...current, ...patch };
};

const withVarietyPrices = (variety, prices) => ({
  ...variety,
  VarietyPrices: prices,
  isSoldOut: areAllPricesSoldOut(prices),
});

const buildAvailabilityPatch = (patch, event, idKey, targetId) => {
  const next = patch ? { ...patch } : {};
  if (targetId != null && next.id == null) {
    next.id = targetId;
  }
  if (hasOwn.call(event, 'isSoldOut') && !hasOwn.call(next, 'isSoldOut')) {
    next.isSoldOut = event.isSoldOut;
  }
  if (idKey && next[idKey] != null && next.id == null) {
    next.id = next[idKey];
  }
  return next;
};

const findVarietyLocation = (products, varietyId) => {
  if (!varietyId) return null;
  const target = normalizeId(varietyId);
  for (let i = 0; i < products.length; i += 1) {
    const varieties = products[i]?.Varieties || [];
    for (let j = 0; j < varieties.length; j += 1) {
      if (normalizeId(varieties[j]?.id) === target) {
        return { productIndex: i, varietyIndex: j };
      }
    }
  }
  return null;
};

const findPriceLocation = (products, priceId) => {
  if (!priceId) return null;
  const target = normalizeId(priceId);
  for (let i = 0; i < products.length; i += 1) {
    const varieties = products[i]?.Varieties || [];
    for (let j = 0; j < varieties.length; j += 1) {
      const prices = varieties[j]?.VarietyPrices || [];
      for (let k = 0; k < prices.length; k += 1) {
        if (normalizeId(prices[k]?.id) === target) {
          return { productIndex: i, varietyIndex: j, priceIndex: k };
        }
      }
    }
  }
  return null;
};

const updateProductAt = (products, index, patch) => {
  const next = [...products];
  const current = products[index];
  next[index] = mergeProduct(current, patch);
  return next;
};

const updateVarietyAt = (products, productIndex, varietyIndex, patch) => {
  const product = products[productIndex];
  const nextVarieties = [...(product?.Varieties || [])];
  nextVarieties[varietyIndex] = mergeVariety(nextVarieties[varietyIndex], patch);
  const nextProduct = { ...product, Varieties: nextVarieties };
  const nextProducts = [...products];
  nextProducts[productIndex] = nextProduct;
  return nextProducts;
};

const updatePriceAt = (products, productIndex, varietyIndex, priceIndex, patch) => {
  const product = products[productIndex];
  const varieties = [...(product?.Varieties || [])];
  const variety = varieties[varietyIndex];
  const prices = [...(variety?.VarietyPrices || [])];
  prices[priceIndex] = mergePrice(prices[priceIndex], patch);
  varieties[varietyIndex] = withVarietyPrices(variety, prices);
  const nextProduct = { ...product, Varieties: varieties };
  const nextProducts = [...products];
  nextProducts[productIndex] = nextProduct;
  return nextProducts;
};

export const applyProductEvent = (products, event) => {
  const list = Array.isArray(products) ? products : [];
  if (!event) return null;
  const action = event.action || event.type;

  switch (action) {
    case 'create-product': {
      const product = event.product || event.productPatch;
      if (!product) return null;
      const targetId = normalizeId(product.id || event.productId);
      if (!targetId) return null;
      const existingIndex = list.findIndex((item) => normalizeId(item?.id) === targetId);
      if (existingIndex !== -1) {
        return updateProductAt(list, existingIndex, product);
      }
      return [...list, { ...product, Varieties: product.Varieties || [] }];
    }
    case 'update-product': {
      const product = event.product || event.productPatch;
      const targetId = normalizeId(product?.id || event.productId);
      if (!targetId) return null;
      const index = list.findIndex((item) => normalizeId(item?.id) === targetId);
      if (index === -1) {
        if (!product) return null;
        return [...list, { ...product, id: product?.id ?? event.productId, Varieties: product.Varieties || [] }];
      }
      return updateProductAt(list, index, product || { id: event.productId });
    }
    case 'delete-product': {
      const targetId = normalizeId(event.productId);
      if (!targetId) return null;
      const next = list.filter((item) => normalizeId(item?.id) !== targetId);
      return next.length === list.length ? list : next;
    }
    case 'update-variety-groups': {
      const targetId = normalizeId(event.productId);
      if (!targetId) return null;
      const index = list.findIndex((item) => normalizeId(item?.id) === targetId);
      if (index === -1) return null;
      return updateProductAt(list, index, {
        id: event.productId,
        VarietyGroups: event.varietyGroups || event.groups || [],
      });
    }
    case 'add-variety': {
      const variety = event.variety || event.varietyPatch;
      const productId = event.productId || variety?.productId || variety?.ProductId;
      if (!variety || !productId) return null;
      const productIndex = list.findIndex((item) => normalizeId(item?.id) === normalizeId(productId));
      if (productIndex === -1) return null;
      const product = list[productIndex];
      const varieties = [...(product?.Varieties || [])];
      const existingIndex = varieties.findIndex((item) => normalizeId(item?.id) === normalizeId(variety.id));
      if (existingIndex === -1) {
        varieties.push(variety);
      } else {
        varieties[existingIndex] = mergeVariety(varieties[existingIndex], variety);
      }
      const nextProduct = { ...product, Varieties: varieties };
      const nextProducts = [...list];
      nextProducts[productIndex] = nextProduct;
      return nextProducts;
    }
    case 'update-variety':
    case 'variety-sold-out':
    case 'variety-available': {
      const variety = event.variety || event.varietyPatch;
      const targetId = normalizeId(variety?.id || event.varietyId);
      if (!targetId) return null;
      const location = findVarietyLocation(list, targetId);
      if (!location) return null;
      const patch = buildAvailabilityPatch(variety, event, 'varietyId', targetId);
      if (
        (action === 'variety-sold-out' || action === 'variety-available') &&
        hasOwn.call(event, 'isSoldOut') &&
        !hasOwn.call(patch, 'VarietyPrices')
      ) {
        const product = list[location.productIndex];
        const currentVariety = product?.Varieties?.[location.varietyIndex];
        patch.VarietyPrices = (currentVariety?.VarietyPrices || []).map((price) => ({
          ...price,
          isSoldOut: event.isSoldOut,
        }));
      }
      return updateVarietyAt(
        list,
        location.productIndex,
        location.varietyIndex,
        patch
      );
    }
    case 'delete-variety': {
      const targetId = normalizeId(event.varietyId);
      if (!targetId) return null;
      const location = findVarietyLocation(list, targetId);
      if (!location) return list;
      const product = list[location.productIndex];
      const varieties = (product?.Varieties || []).filter(
        (item) => normalizeId(item?.id) !== targetId
      );
      const nextProduct = { ...product, Varieties: varieties };
      if (Array.isArray(product?.VarietyGroups)) {
        nextProduct.VarietyGroups = product.VarietyGroups.map((group) => ({
          ...group,
          varietyIds: (group?.varietyIds || []).filter(
            (varietyId) => normalizeId(varietyId) !== targetId
          ),
        }));
      }
      const nextProducts = [...list];
      nextProducts[location.productIndex] = nextProduct;
      return nextProducts;
    }
    case 'add-variety-price': {
      const price = event.price || event.pricePatch;
      const varietyId = event.varietyId || price?.varietyId || price?.VarietyId;
      if (!price || !varietyId) return null;
      const location = findVarietyLocation(list, varietyId);
      if (!location) return null;
      const product = list[location.productIndex];
      const varieties = [...(product?.Varieties || [])];
      const variety = varieties[location.varietyIndex];
      const prices = [...(variety?.VarietyPrices || [])];
      const existingIndex = prices.findIndex((item) => normalizeId(item?.id) === normalizeId(price.id));
      if (existingIndex === -1) {
        prices.push(price);
      } else {
        prices[existingIndex] = mergePrice(prices[existingIndex], price);
      }
      varieties[location.varietyIndex] = withVarietyPrices(variety, prices);
      const nextProduct = { ...product, Varieties: varieties };
      const nextProducts = [...list];
      nextProducts[location.productIndex] = nextProduct;
      return nextProducts;
    }
    case 'update-variety-price':
    case 'price-sold-out':
    case 'price-available': {
      const price = event.price || event.pricePatch;
      const targetId = normalizeId(price?.id || event.priceId);
      if (!targetId) return null;
      let location = findPriceLocation(list, targetId);
      if (!location && (event.varietyId || price?.varietyId)) {
        const varietyLocation = findVarietyLocation(list, event.varietyId || price?.varietyId);
        if (varietyLocation) {
          const product = list[varietyLocation.productIndex];
          const variety = product?.Varieties?.[varietyLocation.varietyIndex];
          const prices = variety?.VarietyPrices || [];
          const priceIndex = prices.findIndex((item) => normalizeId(item?.id) === targetId);
          if (priceIndex !== -1) {
            location = { ...varietyLocation, priceIndex };
          }
        }
      }
      if (!location) return null;
      return updatePriceAt(
        list,
        location.productIndex,
        location.varietyIndex,
        location.priceIndex,
        buildAvailabilityPatch(price, event, 'varietyPriceId', targetId)
      );
    }
    case 'delete-variety-price': {
      const targetId = normalizeId(event.priceId);
      if (!targetId) return null;
      const location = findPriceLocation(list, targetId);
      if (!location) return list;
      const product = list[location.productIndex];
      const varieties = [...(product?.Varieties || [])];
      const variety = varieties[location.varietyIndex];
      const prices = (variety?.VarietyPrices || []).filter(
        (item) => normalizeId(item?.id) !== targetId
      );
      varieties[location.varietyIndex] = withVarietyPrices(variety, prices);
      const nextProduct = { ...product, Varieties: varieties };
      const nextProducts = [...list];
      nextProducts[location.productIndex] = nextProduct;
      return nextProducts;
    }
    default:
      return null;
  }
};
